/**
 * Branding em RUNTIME (white-label): busca GET /operadora/branding (publico,
 * o Login usa antes do auth) e expoe a marca da operadora pro app inteiro.
 * Enquanto carrega (ou se a API falhar), usa os defaults de build do
 * branding.js — o comportamento atual nao muda em nada.
 *
 * O admin edita em /admin/operadora; nada de rebuild pra trocar marca.
 *
 * Sem piscar (20/09/2026): antes a tela nascia com o logo de fabrica do
 * produto e trocava para o da operadora quando a API respondia — tres estados
 * visiveis (AURA, troca, operadora), piores quanto mais lenta a rede. Agora:
 *  1. o ultimo branding recebido fica guardado no navegador e e o estado
 *     inicial da proxima visita (abre direto com a marca certa);
 *  2. `pronto` diz se ja ha branding confiavel (cache ou resposta da API);
 *     Login e header so desenham o logo quando `pronto` — na primeira visita
 *     de um navegador aparece um espaco do mesmo tamanho, nunca o logo errado.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { BRAND_NAME, BRAND_LOGO, BRAND_LOGO_LOGIN } from '../branding';
import { derive } from '../utils/brandColor';

const defaults = {
  nome: BRAND_NAME,
  nome_operadora: BRAND_NAME,
  tagline: '',
  cor_primaria: '#20a490',
  logo_url: '',            // vazio => assets de build (BRAND_LOGO*)
  razao_social: '',
  cnpj: '',
  endereco: '',
  telefone: '',
  registro_ans: '',
  email_from_name: BRAND_NAME,
  demo: false,             // AURA_DEMO=1 => esconde detalhes internos (ex.: Radar)
};

const CHAVE_CACHE = 'aura_branding';

function lerCache() {
  try {
    const bruto = localStorage.getItem(CHAVE_CACHE);
    const dados = bruto ? JSON.parse(bruto) : null;
    return dados && typeof dados === 'object' ? dados : null;
  } catch (e) { return null; } // storage bloqueado/privado: segue sem cache
}

function gravarCache(dados) {
  try { localStorage.setItem(CHAVE_CACHE, JSON.stringify(dados)); } catch (e) { /* sem storage */ }
}

const BrandingContext = createContext({ branding: defaults, pronto: false, recarregar: () => {} });

export function BrandingProvider({ children }) {
  const [estado, setEstado] = useState(() => {
    const cache = lerCache();
    return { branding: { ...defaults, ...(cache || {}) }, pronto: !!cache };
  });
  const { branding, pronto } = estado;

  const recarregar = () => {
    // axios "cru" (sem interceptors de auth): endpoint publico, e o Login
    // precisa dele antes de existir token.
    axios.get(`${API_URL}/operadora/branding`)
      .then((r) => { gravarCache(r.data); setEstado({ branding: { ...defaults, ...r.data }, pronto: true }); })
      .catch(() => setEstado((e) => ({ ...e, pronto: true }))); // API fora: mostra o que tem (cache ou fabrica) — nunca quebra o app por branding
  };

  useEffect(() => { recarregar(); }, []);

  // título da aba = "Nome do sistema" da marca (antes: 'Aquario CRM' fixo do build)
  useEffect(() => { document.title = branding.nome || BRAND_NAME; }, [branding.nome]);

  // White-label da cor: de UMA cor primaria derivamos a paleta inteira e a
  // publicamos como CSS variables no :root. Trocar a cor na tela da operadora
  // repinta o sistema sem rebuild.
  //
  // Duas coisas que antes faltavam e quebravam o white-label:
  //  1. `--brand-ink`: a cor de TEXTO sobre a marca, calculada por contraste
  //     (WCAG). Antes o texto era `#fff` fixo e sumia em cores claras (amarelo).
  //  2. os aliases `--primary*`: metade do app (header, botoes, sidebar, kanban
  //     via index.css) pinta com `--primary`, que era estatico e nunca mudava.
  //     Apontar `--primary*` pros tons derivados faz esses elementos passarem a
  //     seguir a marca — o estilo inline no :root vence o do index.css.
  useEffect(() => {
    const root = document.documentElement.style;
    const p = derive(branding.cor_primaria) || derive('#20a490');
    root.setProperty('--brand', p.brand);
    root.setProperty('--brand-ink', p.ink);     // texto/icone SOBRE a marca
    root.setProperty('--brand-dark', p.dark);    // hover, gradiente
    root.setProperty('--brand-light', p.light);
    root.setProperty('--brand-soft', p.soft);    // fundo suave (tint)
    // aliases: unificam o sistema `--primary*` antigo com a marca derivada
    root.setProperty('--primary', p.brand);
    root.setProperty('--primary-dark', p.dark);
    root.setProperty('--primary-light', p.light);
  }, [branding.cor_primaria]);

  return (
    <BrandingContext.Provider value={{ branding, pronto, recarregar }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

/**
 * Resolve a URL de um arquivo enviado (uploads/*).
 *
 * ATENCAO: os uploads sao servidos na RAIZ do backend (/uploads/...), NUNCA
 * sob o prefixo da API. Em deploy padrao API_URL='/api', e concatenar dava
 * /api/uploads/... => 404 (logo da operadora nao aparecia). Quando API_URL e'
 * uma ORIGEM completa (dev local: http://localhost:8000), aí sim prefixa.
 */
export function urlUpload(caminho) {
  if (!caminho) return caminho;
  return /^https?:\/\//i.test(API_URL) ? `${API_URL}${caminho}` : caminho;
}

/** Logo do header: da operadora (runtime) ou o asset de build. */
export function logoHeader(branding) {
  return branding.logo_url ? urlUpload(branding.logo_url) : BRAND_LOGO;
}

/** Logo da tela de login: idem. */
export function logoLogin(branding) {
  return branding.logo_url ? urlUpload(branding.logo_url) : BRAND_LOGO_LOGIN;
}
